import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Eyebrow } from "@/components/Eyebrow";
import { AnimatedNumber } from "@/components/AnimatedNumber";

const BLOCKS = [
    {
        title: "Challenge",
        body: "Low Vacancy fills vacant units for property managers. Their lead flow was stuck in inboxes and spreadsheets. Every inquiry meant manually copying information into Excel, slowing down operations, and each client report took 4 to 6 hours to generate.",
    },
    {
        title: "Solution",
        body: "A branded lead capture system wired straight into their new back end, a live performance dashboard, and the automations that move a lead from intake form to detailed reporting without a human touching it.",
    },
];

const RESULT_NUMBER = 85;
const RESULT_LABEL = "hours per month regained";

const TESTIMONIAL =
    "\u201CWe've been able to take on more clients without increasing our headcount thanks to ReplAIced. I estimate we've regained approximately 85 hours per month. I don't have time to stay on top of every new AI tool that comes out, but I really appreciate that Tai researches the ones relevant to our business and helps us implement them in a fraction of the time it would have taken me on my own.\u201D";

export function Proof() {
    const counterRef = useRef(null);
    const inView = useInView(counterRef, { once: true, margin: "-80px" });

    return (
        <section id="proof" data-testid="proof" className="py-20 sm:py-24 bg-surface border-y border-border">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <Eyebrow>Proof</Eyebrow>
                <h2 className="mt-5 max-w-4xl font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.08]">
                    Low Vacancy stopped chasing leads. The system chases them now<span className="period">.</span>
                </h2>

                <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-16">
                    {BLOCKS.map((b, i) => (
                        <motion.div
                            key={b.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                        >
                            <h3 className="font-display font-semibold text-xl tracking-tight mb-3">{b.title}</h3>
                            <p className="text-muted-foreground leading-relaxed">{b.body}</p>
                        </motion.div>
                    ))}
                </div>

                {/* result counter */}
                <div ref={counterRef} className="mt-16 rounded-3xl border border-border bg-background p-8 sm:p-14">
                    <h3 className="font-display font-semibold text-xl tracking-tight mb-6">Result</h3>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-8">
                        <span data-testid="proof-counter" className="font-display font-bold tracking-tight text-7xl sm:text-8xl leading-none tabular-nums">
                            <AnimatedNumber value={RESULT_NUMBER} from={0} run={inView} />
                        </span>
                        <span className="text-xl sm:text-2xl text-muted-foreground pb-2 sm:pb-3">{RESULT_LABEL}</span>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground/70">Regained by a three person team.</p>
                    <p className="mt-8 text-lg text-foreground/90 leading-relaxed max-w-2xl">
                        Now it runs on retainer, and it keeps getting sharper every month.
                    </p>
                </div>

                {/* testimonial */}
                <motion.figure
                    data-testid="proof-testimonial"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6 }}
                    className="mt-10 border-l-2 border-foreground/20 pl-6 sm:pl-8 max-w-3xl"
                >
                    <blockquote className="text-lg sm:text-xl text-foreground/90 leading-relaxed">
                        {TESTIMONIAL}
                    </blockquote>
                    <figcaption className="mt-6 text-sm text-muted-foreground">
                        Jack Campbell, Low Vacancy
                    </figcaption>
                </motion.figure>

                <p className="mt-10 text-sm text-muted-foreground">
                    Low Vacancy · Lead Generation, Vancouver BC.
                </p>
            </div>
        </section>
    );
}
